import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpTrayIcon,
  DocumentIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

const IFC_EXTENSION = /\.ifc$/i;

const baseName = (fileName) => String(fileName || "").replace(/\.[^/.]+$/, "");

/**
 * Upload dialog for IFC models: the user picks the files, the discipline and
 * (optionally) the house before anything is sent to the API, so a model never
 * lands in the wrong bucket because an inline select was left on its default.
 */
export function IfcUploadDialog({
  open,
  houses = [],
  disciplines = [],
  uploading = false,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [discipline, setDiscipline] = useState("");
  const [houseId, setHouseId] = useState("");
  const [name, setName] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setDiscipline("");
    setHouseId("");
    setName("");
    setDragActive(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !uploading) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, uploading, onClose]);

  const addFiles = useCallback((incoming) => {
    const ifcFiles = Array.from(incoming || []).filter((file) =>
      IFC_EXTENSION.test(file.name),
    );
    if (ifcFiles.length === 0) return;
    setFiles((current) => {
      const seen = new Set(current.map((file) => `${file.name}:${file.size}`));
      const added = ifcFiles.filter(
        (file) => !seen.has(`${file.name}:${file.size}`),
      );
      return [...current, ...added];
    });
  }, []);

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (uploading) return;
    addFiles(event.dataTransfer?.files);
  };

  const removeFile = (index) =>
    setFiles((current) => current.filter((_, i) => i !== index));

  // A single file may be renamed; with several files the filenames are kept.
  const namePlaceholder = useMemo(
    () => (files.length === 1 ? baseName(files[0].name) : ""),
    [files],
  );

  const canConfirm = files.length > 0 && Boolean(discipline) && !uploading;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm?.({
      files,
      discipline,
      drawing_house_id: houseId || null,
      name: files.length === 1 && name.trim() ? name.trim() : null,
    });
  };

  if (!open) return null;

  const disciplineLabel = (code) =>
    t(`ifc_viewer.discipline.${code}`, { defaultValue: code });

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !uploading) onClose?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("ifc_viewer.upload_dialog_title")}
        className="max-h-full w-full max-w-lg overflow-y-auto rounded-xl border border-[#e5e8ec] bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-[#e5e8ec] px-4 py-3">
          <h2 className="m-0 text-sm font-semibold text-[#14171c]">
            {t("ifc_viewer.upload_dialog_title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            aria-label={t("Cancel")}
            className="grid h-8 w-8 place-items-center rounded-lg text-[#59616d] hover:bg-[#eef0f3] disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-4 px-4 py-4">
          <div
            onDragOver={(event) => {
              event.preventDefault();
              if (!uploading) setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            className={`flex flex-col items-center gap-2 rounded-[12px] border-2 border-dashed px-4 py-6 text-center transition-colors ${
              dragActive
                ? "border-[#2b6ef0] bg-[#f2f6fe]"
                : "border-[#e5e8ec] bg-[#f6f7f9]"
            }`}
          >
            <ArrowUpTrayIcon className="h-6 w-6 text-[#969da8]" aria-hidden="true" />
            <p className="m-0 text-[13px] text-[#59616d]">
              {t("ifc_viewer.upload_drop_hint")}
            </p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="rounded-[8px] border border-[#e5e8ec] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#14171c] hover:bg-[#eef0f3] disabled:opacity-50"
            >
              {t("ifc_viewer.upload_choose_files")}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".ifc"
              multiple
              className="hidden"
              aria-label={t("ifc_viewer.upload_choose_files")}
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </div>

          {files.length > 0 && (
            <div className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
                {t("ifc_viewer.upload_selected", { count: files.length })}
              </span>
              <ul className="m-0 list-none space-y-1 p-0">
                {files.map((file, index) => (
                  <li
                    key={`${file.name}-${file.size}-${index}`}
                    className="flex items-center gap-2 rounded-[8px] border border-[#e5e8ec] bg-white px-2.5 py-1.5"
                  >
                    <DocumentIcon
                      className="h-4 w-4 shrink-0 text-[#969da8]"
                      aria-hidden="true"
                    />
                    <span
                      className="flex-1 truncate text-[12px] text-[#14171c]"
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                      aria-label={t("ifc_viewer.upload_remove_file", {
                        name: file.name,
                      })}
                      className="grid h-6 w-6 place-items-center rounded text-[#969da8] hover:bg-[#eef0f3] hover:text-[#14171c] disabled:opacity-50"
                    >
                      <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
              {t("ifc_viewer.discipline_label")} *
            </span>
            <select
              value={discipline}
              onChange={(event) => setDiscipline(event.target.value)}
              disabled={uploading}
              className="w-full rounded-lg border border-[#e5e8ec] bg-white px-3 py-2 text-[13px] text-[#14171c] outline-none focus:border-[#2b6ef0] disabled:opacity-50"
            >
              <option value="">{t("ifc_viewer.discipline_required")}</option>
              {disciplines.map((code) => (
                <option key={code} value={code}>
                  {disciplineLabel(code)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
              {t("ifc_viewer.house_label")} · {t("ifc_viewer.house_optional")}
            </span>
            <select
              value={houseId}
              onChange={(event) => setHouseId(event.target.value)}
              disabled={uploading || houses.length === 0}
              className="w-full rounded-lg border border-[#e5e8ec] bg-white px-3 py-2 text-[13px] text-[#14171c] outline-none focus:border-[#2b6ef0] disabled:opacity-50"
            >
              <option value="">{t("ifc_viewer.house_whole_project")}</option>
              {houses.map((house) => (
                <option key={house.id} value={house.id}>
                  {house.name}
                </option>
              ))}
            </select>
          </label>

          {files.length === 1 && (
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
                {t("ifc_viewer.upload_name_label")}
              </span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={namePlaceholder}
                disabled={uploading}
                className="w-full rounded-lg border border-[#e5e8ec] bg-white px-3 py-2 text-[13px] text-[#14171c] outline-none focus:border-[#2b6ef0] disabled:opacity-50"
              />
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-[#e5e8ec] px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="rounded-lg border border-[#e5e8ec] px-3 py-2 text-sm font-semibold text-[#14171c] hover:bg-[#f6f7f9] disabled:opacity-50"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="rounded-lg bg-[#2b6ef0] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {uploading
              ? t("ifc_viewer.uploading")
              : t("ifc_viewer.upload_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default IfcUploadDialog;
